import { memo, useRef, type ReactElement } from 'react';
import classnames from 'classnames';

import { WithChildren, WithError } from '@eva/types/dto';

import type {
  RearrangeHandler,
  GroupedTableClassNames,
  GroupRowState,
  ControlDescriptor,
  RowState,
  GetFormFunction,
  GetPlaceholderFunction,
} from '../types';

import Group from '../Group';
import PlainList from '../PlainList';
import { useDndList } from '../hooks';

import './GroupWithChildren.scss';

export interface GroupWithChildrenProps<I, G> {
  group: G;
  groups: G[];
  dndEnabled?: boolean;
  dragInProcess?: boolean;
  selectedItem?: I;
  classNames: GroupedTableClassNames;
  groupState: GroupRowState;
  itemStates: { [key: number]: RowState };
  itemControls?: ControlDescriptor<I, G>[];
  groupControls?: ControlDescriptor<G, G>[];
  getItemId: (item: I) => number;
  getItemLabel: (item: I) => ReactElement | string;
  getItemError?: (error: string, item: I) => ReactElement | string;
  getGroupId: (group: G) => number;
  getGroupLabel: (group: G) => ReactElement | string;
  getGroupError?: (error: string, group: G) => ReactElement | string;
  getItemForm: GetFormFunction<I, RowState>;
  getGroupForm: GetFormFunction<G, GroupRowState>;
  isItemSortable: (item: I) => boolean;
  getItemPlaceholder?: GetPlaceholderFunction<I, RowState>;
  onExpand: (group: G) => void;
  onCollapse: (group: G) => void;
  onRearrange: RearrangeHandler;
  updateItemState: (item: I, updates: Partial<RowState>) => void;
  updateGroupState: (group: G, updates: Partial<GroupRowState>) => void;
}

const isWithChildren = <T, E extends WithChildren<T>>(entity: E | unknown): entity is WithChildren<T> =>
  Boolean((entity as WithChildren<T>)?.children);

const GroupWithChildren = <C, G extends WithChildren<C> & WithError>({
  group,
  groups,
  dndEnabled,
  classNames,
  groupState,
  itemStates,
  itemControls,
  groupControls,
  dragInProcess,
  selectedItem,
  getItemId,
  getGroupId,
  getItemLabel,
  getItemError,
  getGroupLabel,
  getGroupError,
  getItemForm,
  getGroupForm,
  getItemPlaceholder,
  onExpand,
  onCollapse,
  onRearrange,
  updateItemState,
  updateGroupState,
  isItemSortable,
}: GroupWithChildrenProps<C, G>) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { handlerId, drop } = useDndList(group.children, getGroupId(group), onRearrange);

  drop(containerRef);

  const groupId = getGroupId(group);

  if (groupId == null && !isWithChildren(group)) {
    return null;
  }

  const isConfirming = groupState?.isConfirming;

  const groupClassNames = classnames({
    [classNames.groupWithChildrenClassName]: true,
    [classNames.groupWithChildrenIsConfirmingClassName]: isConfirming,
  });

  return (
    <div ref={containerRef} className={groupClassNames} data-handler-id={handlerId}>
      <Group
        key={groupId}
        group={group}
        groups={groups}
        groupState={groupState}
        classNames={classNames}
        controls={groupControls}
        getGroupId={getGroupId}
        getGroupForm={getGroupForm}
        getGroupLabel={getGroupLabel}
        getGroupError={getGroupError}
        onExpand={onExpand}
        onCollapse={onCollapse}
        updateGroupState={updateGroupState}
      />

      {groupState?.isExpanded && (
        <PlainList
          groups={groups}
          groupId={groupId}
          dndEnabled={dndEnabled}
          items={group.children}
          itemStates={itemStates}
          itemControls={itemControls}
          classNames={classNames}
          selectedItem={selectedItem}
          dragInProcess={dragInProcess}
          getItemId={getItemId}
          getItemForm={getItemForm}
          getItemLabel={getItemLabel}
          getItemError={getItemError}
          isItemSortable={isItemSortable}
          getItemPlaceholder={getItemPlaceholder}
          onRearrange={onRearrange}
          updateItemState={updateItemState}
        />
      )}

      <div className="clearfix" />
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default memo(GroupWithChildren);
